/**
 * batch-61 loonstrook-dedupe (FO-456) - dependency-vrije, synchrone SHA-256.
 *
 * Waarom een eigen implementatie: `@streep/utils` wordt via de barrel óók in
 * browser-bundels geladen (backoffice/portal-client-componenten), dus een
 * `node:crypto`-import mag hier niet (zie de `encryption.ts`-uitzondering), en
 * `globalThis.crypto.subtle` is asynchroon terwijl de vingerafdruk-functie een
 * pure, synchrone functie moet zijn. SHA-256 is klein genoeg om hier compact en
 * getest te houden (FIPS 180-4; testvectoren in `sha256.test.ts`).
 *
 * Alleen voor identificatie/dedupe-sleutels (inhoud-vingerafdruk), NIET voor
 * wachtwoorden of geheimen: daarvoor blijft `@streep/utils/encryption`.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])

/** UTF-8-encodeer een string naar bytes (TextEncoder is overal beschikbaar: Node 20 + browsers). */
function naarBytes(input: string): Uint8Array {
  return new TextEncoder().encode(input)
}

function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n))
}

/**
 * SHA-256 van een UTF-8-string, als 64-teken lowercase hex.
 * Zelfde uitkomst als `createHash('sha256').update(input, 'utf8').digest('hex')`.
 */
export function sha256Hex(input: string): string {
  const msg = naarBytes(input)
  const bitLen = msg.length * 8

  // Padding: 0x80, nullen tot (len mod 64) == 56, dan 64-bit big-endian lengte.
  const padLen = (((msg.length + 9 + 63) >> 6) << 6)
  const padded = new Uint8Array(padLen)
  padded.set(msg)
  padded[msg.length] = 0x80
  // Lengte in bits (we blijven ver onder 2^53, dus twee 32-bit woorden volstaan).
  const hi = Math.floor(bitLen / 0x100000000)
  const lo = bitLen >>> 0
  padded[padLen - 8] = (hi >>> 24) & 0xff
  padded[padLen - 7] = (hi >>> 16) & 0xff
  padded[padLen - 6] = (hi >>> 8) & 0xff
  padded[padLen - 5] = hi & 0xff
  padded[padLen - 4] = (lo >>> 24) & 0xff
  padded[padLen - 3] = (lo >>> 16) & 0xff
  padded[padLen - 2] = (lo >>> 8) & 0xff
  padded[padLen - 1] = lo & 0xff

  let h0 = 0x6a09e667
  let h1 = 0xbb67ae85
  let h2 = 0x3c6ef372
  let h3 = 0xa54ff53a
  let h4 = 0x510e527f
  let h5 = 0x9b05688c
  let h6 = 0x1f83d9ab
  let h7 = 0x5be0cd19

  const w = new Uint32Array(64)
  for (let offset = 0; offset < padLen; offset += 64) {
    for (let i = 0; i < 16; i++) {
      const j = offset + i * 4
      w[i] =
        ((padded[j] << 24) | (padded[j + 1] << 16) | (padded[j + 2] << 8) | padded[j + 3]) >>> 0
    }
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3)
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10)
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0
    }

    let a = h0
    let b = h1
    let c = h2
    let d = h3
    let e = h4
    let f = h5
    let g = h6
    let h = h7

    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)
      const ch = (e & f) ^ (~e & g)
      const t1 = (h + S1 + ch + K[i] + w[i]) >>> 0
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) >>> 0
      h = g
      g = f
      f = e
      e = (d + t1) >>> 0
      d = c
      c = b
      b = a
      a = (t1 + t2) >>> 0
    }

    h0 = (h0 + a) >>> 0
    h1 = (h1 + b) >>> 0
    h2 = (h2 + c) >>> 0
    h3 = (h3 + d) >>> 0
    h4 = (h4 + e) >>> 0
    h5 = (h5 + f) >>> 0
    h6 = (h6 + g) >>> 0
    h7 = (h7 + h) >>> 0
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7].map((x) => x.toString(16).padStart(8, '0')).join('')
}
